import { isServer } from '@cleartrip/ct-platform-utils'
import { rumEvents } from 'the-room-of-requirement'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getCookie } from '@/utils/general/cookie'
import { isUserSignedIn } from '@/utils/general/user'

let currentPage = 'unknown'
let isRumConfigured = false

// ─── Event names ──────────────────────────────────────────────────────────────
dayjs.extend(customParseFormat)

export const RUM_EVENT = {
  PAGE_VIEW: 'PAGE_VIEW',
  // Home
  HOME_LOADED: 'HOME_LOADED',
  // SRP
  SEARCH_RESULTS_LOADED: 'SEARCH_RESULTS_LOADED',
  SEARCH_RESULTS_EMPTY: 'SEARCH_RESULTS_EMPTY',
  SEARCH_FAILED: 'SEARCH_FAILED',
  SEARCH_MODIFIED: 'SEARCH_MODIFIED',
  SRP_BOOK_CLICKED: 'SRP_BOOK_CLICKED',
  // Interstitial
  ITINERARY_CREATED: 'ITINERARY_CREATED',
  ITINERARY_CREATE_FAILED: 'ITINERARY_CREATE_FAILED',
  // Itinerary
  ITINERARY_LOADED: 'ITINERARY_LOADED',
  ITINERARY_LOAD_FAILED: 'ITINERARY_LOAD_FAILED',
  FLIGHT_NOT_AVAILABLE: 'FLIGHT_NOT_AVAILABLE',
  INTERSTITIAL_LOADED: 'INTERSTITIAL_LOADED',
  INTERSTITIAL_BOOK_CLICKED: 'INTERSTITIAL_BOOK_CLICKED',
  // Addons
  ADDONS_LOADED: 'ADDONS_LOADED',
  ADDONS_LOAD_FAILED: 'ADDONS_LOAD_FAILED',
  // Travellers
  TRAVELLERS_LOADED: 'TRAVELLERS_LOADED',
  TRAVELLERS_UPDATE_SUCCESS: 'TRAVELLERS_UPDATE_SUCCESS',
  TRAVELLERS_UPDATE_FAILED: 'TRAVELLERS_UPDATE_FAILED',
  // Pre-payment (shared across travellers / addons pages)
  PREPAYMENT_INITIATED: 'PREPAYMENT_INITIATED',
  PREPAYMENT_SUCCESS: 'PREPAYMENT_SUCCESS',
  PREPAYMENT_FAILED: 'PREPAYMENT_FAILED',
  // Confirmation
  CONFIRMATION_LOADED: 'CONFIRMATION_LOADED',
  CONFIRMATION_POLLING_EXCEEDED: 'CONFIRMATION_POLLING_EXCEEDED',
  CONFIRMATION_FAILED: 'CONFIRMATION_FAILED',
  // Amendment
  AMENDMENT_SEARCH_LOADED: 'AMENDMENT_SEARCH_LOADED',
  AMENDMENT_SEARCH_FAILED: 'AMENDMENT_SEARCH_FAILED',
  AMENDMENT_ITINERARY_CREATED: 'AMENDMENT_ITINERARY_CREATED',
  AMENDMENT_ITINERARY_CREATE_FAILED: 'AMENDMENT_ITINERARY_CREATE_FAILED',
  AMENDMENT_PREPAYMENT_SUCCESS: 'AMENDMENT_PREPAYMENT_SUCCESS',
  AMENDMENT_PREPAYMENT_FAILED: 'AMENDMENT_PREPAYMENT_FAILED',
} as const

export type RumEventName = (typeof RUM_EVENT)[keyof typeof RUM_EVENT]

// ─── Failure reasons ──────────────────────────────────────────────────────────

export const RUM_FAILURE_REASON = {
  ITINERARY_LOAD_FAILED: 'ITINERARY_LOAD_FAILED',
  EMPTY_RESPONSE: 'empty_response',
  API_ERROR: 'api_error',
  EXCEPTION: 'exception',
  SSR_NULL: 'ssr_null',
  INVALID_PARAMS: 'invalid_params',
  PAST_DATE: 'past_date',
  MISSING_DATE: 'missing_date',
  EMPTY_ITINERARY_ID: 'empty_itinerary_id',
  EMPTY_TRIP_ID: 'empty_trip_id',
  FLIGHT_NOT_AVAILABLE: 'flight_not_available',
  PRICE_CHANGED: 'price_changed',
  PARTIAL_SUCCESS: 'partial_success',
  COUPON_NOT_APPLICABLE: 'coupon_not_applicable',
  PAY_URL_GENERATION_FAILED: 'pay_url_generation_failed',
  DIALOGUE_INTERRUPT: 'dialogue_interrupt',
  NOT_PROCESSED: 'not_processed',
} as const

export type RumFailureReason = (typeof RUM_FAILURE_REASON)[keyof typeof RUM_FAILURE_REASON]

// ─── Funnel steps ─────────────────────────────────────────────────────────────

const FUNNEL_STEP: Partial<Record<RumEventName, number>> = {
  [RUM_EVENT.HOME_LOADED]: 1,
  [RUM_EVENT.SEARCH_RESULTS_LOADED]: 2,
  [RUM_EVENT.ITINERARY_CREATED]: 3,
  [RUM_EVENT.ITINERARY_LOADED]: 4,
  [RUM_EVENT.TRAVELLERS_UPDATE_SUCCESS]: 5,
  [RUM_EVENT.PREPAYMENT_SUCCESS]: 6,
  [RUM_EVENT.CONFIRMATION_LOADED]: 7,
}

// ─── Utilities ────────────────────────────────────────────────────────────────

const getDeviceId = (): string => {
  if (isServer()) return ''
  try {
    return getCookie('ct-dvId') || window.localStorage.getItem('dvid') || ''
  } catch {
    return ''
  }
}

export function getDaysToJourneyBucket(departDate?: string): string {
  if (!departDate) return 'unknown'
  const parsed = dayjs(departDate, 'DD/MM/YYYY', true)
  if (!parsed.isValid()) return 'unknown'
  const days = parsed.startOf('day').diff(dayjs().startOf('day'), 'day')
  if (days < 0)   return 'past'
  if (days <= 4)  return '0-4'
  if (days <= 10) return '5-10'
  if (days <= 20) return '11-20'
  return '20+'
}

export function getSrpRumContext(processedData: {
  isIntl?: boolean
  returnDate?: string
  departDate?: string
}) {
  return {
    isIntl: processedData.isIntl,
    tripType: processedData.returnDate ? 'RT' : 'OW',
    daysToJourney: getDaysToJourneyBucket(processedData.departDate),
  }
}

// ─── Setup ────────────────────────────────────────────────────────────────────

export function configureRumEvents(): void {
  if (isServer() || isRumConfigured) return

  const host = window.location.hostname
  if (host.includes('localhost') || host.includes('qa') ) {
    isRumConfigured = true 
    return
  }

  const serviceName = process.env.NEXT_PUBLIC_SERVICE_NAME || 'ct-air-desktop'

  rumEvents.configure({
    endpoint: `${window.location.origin}/events/push`,
    eventType: 'AIR_UI_EVENTS',
    platform: 'DESKTOP_WEB',
    environment: process.env.NEXT_PUBLIC_CT_ENV || 'staging',
    getContext: () => ({
      page: currentPage,
      'service.name': serviceName,
      isLoggedIn: isUserSignedIn(),
      deviceId: getDeviceId(),
      deviceType: 'desktop-web',
    }),
  })
  isRumConfigured = true
}

// ─── Tracking functions ───────────────────────────────────────────────────────

export function setRumPage(page: string): void {
  if (isServer() || !page) return
  currentPage = page
}

export function trackPageView(page: string): void {
  if (isServer() || !page) return
  if (!isRumConfigured) configureRumEvents()
  currentPage = page
  rumEvents.track(RUM_EVENT.PAGE_VIEW, {})
}

export function trackRumEvent(
  eventName: RumEventName,
  payload: Record<string, unknown> = {}
): void {
  if (isServer()) return
  if (!isRumConfigured) configureRumEvents()

  const step = FUNNEL_STEP[eventName]
  // CONFIRMATION_LOADED only counts as funnel completion on BOOKING_SUCCESSFUL
  const completesFunnel =
    eventName !== RUM_EVENT.CONFIRMATION_LOADED ||
    payload.outcome === 'BOOKING_SUCCESSFUL'

  rumEvents.track(eventName, {
    ...payload,
    ...(step !== undefined && completesFunnel ? { funnelStep: step } : {}),
  })
}

const reportedImageUrls = new Set<string>()

/**
 * Global image load-failure tracker → IMAGE_LOAD_FAILURE on /events/push.
 * `error` events on <img> don't bubble, so a capture-phase listener on window
 * sees every failed image (raw <img>, next/image, ImageBlock) without touching
 * call sites. Browsers don't expose the HTTP status for image loads, so this
 * fires for any failure (404, network, CORS). Each URL is reported once per page lifetime.
 */
export function initImageErrorTracking(): () => void {
  const onError = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLImageElement)) return
    const imageUrl = target.currentSrc || target.src
    if (!imageUrl || reportedImageUrls.has(imageUrl)) return
    reportedImageUrls.add(imageUrl)

    if (!isRumConfigured) configureRumEvents()
    rumEvents.track('IMAGE_LOAD_FAILURE', { imageUrl, alt: target.alt })
  }

  window.addEventListener('error', onError, true)
  return () => window.removeEventListener('error', onError, true)
}
